import { useCallback, useEffect, useMemo } from 'react';

import { dispatchGAEvent } from 'utils/googleAnalytics';
import { logEventToHotjar } from 'utils/logging';

import { useDraftSteps } from './useDraftSteps';

import { useDraftProgress } from '../contexts/DraftProgressProvider';
import { useDraftStepSubmitBus } from '../contexts/DraftStepSubmitBus';
import type { DraftSteps } from '../utils/types';

export const useDraftStepProgress = () => {
  const { currentStep, setCurrentStep, validatedSteps, setValidatedSteps } =
    useDraftProgress();

  const steps = useDraftSteps();

  const { onSuccessfulSubmit } = useDraftStepSubmitBus();

  const findStepIndex = useCallback(
    (step: DraftSteps) => {
      return steps.findIndex((_step) => _step === step);
    },
    [steps]
  );

  const next = useCallback(() => {
    setCurrentStep((current) => {
      const currentIndex = findStepIndex(current);
      const nextIndex = Math.min(currentIndex + 1, steps.length - 1);
      logEventToHotjar('new-manual-payout:step_changed');
      dispatchGAEvent('create_manual_payout_step_changed', {
        from: current,
        to: steps[nextIndex],
        version: 'v2'
      });

      return steps[nextIndex];
    });
  }, [findStepIndex, setCurrentStep, steps]);

  const previous = useCallback(() => {
    setCurrentStep((current) => {
      const currentIndex = findStepIndex(current);
      const previousIndex = Math.max(currentIndex - 1, 0);
      return steps[previousIndex];
    });
  }, [findStepIndex, setCurrentStep, steps]);

  const isFinalStep = useMemo(() => {
    return findStepIndex(currentStep) === steps.length - 1;
  }, [findStepIndex, currentStep, steps.length]);

  useEffect(() => {
    return onSuccessfulSubmit((validatedStep) =>
      setValidatedSteps((prev) => prev.add(validatedStep))
    );
  }, [onSuccessfulSubmit, setValidatedSteps]);

  const isStepValidated = useMemo(() => {
    return validatedSteps.has(currentStep);
  }, [currentStep, validatedSteps]);

  return {
    currentStep,
    next,
    previous,
    setStep: setCurrentStep,
    findStepIndex,
    isFinalStep,
    isStepValidated
  };
};
